import {memo, useState} from 'react';

import styled from 'styled-components';

import {media} from '~/shared/theme/media';
import {bodyBold, smallBold, smallRegular} from '~/shared/theme/typography';
import PieButton from '~/shared/theme/pie/PieButton';
import {getLocalizationService} from '~/shared/services/localisationService';
import {trackEvent} from '~/shared/services/analytics';
import CircleTimer from '~/shared/components/CircleTimer';

import TenbisCardLogo from './assets/tenbisCardLogo.svg?react';

const Root = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 82 / 53;
  background-color: ${({theme}) => theme.colors.surface};
  border-radius: 16px;
  box-shadow: ${({theme}) => theme.shadows.cardElevation};
  overflow: hidden;
  ${media.minLargeMobile`
    width: 328px;
    height: 212px;
  `}
`;

const Background = styled.div<{isActive?: boolean}>`
  position: absolute;
  display: flex;
  justify-content: center;
  padding-top: 15.8%;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  aspect-ratio: 41 / 18;
  background-color: ${({theme, isActive}) => (!isActive ? 'rgba(140, 153, 155, 0.50)' : theme.colors.primary)};
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 83.22%);
  z-index: 1;
`;

const ContentOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  padding: 4.88% 4.88% 6.1%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CardHolderNameContainer = styled.div`
  display: flex;
  justify-content: left;
`;

const CardHolderName = styled.span`
  ${bodyBold}
  display: block;
  color: ${({theme}) => theme.colors.surface};
`;

const BottomBlock = styled.div`
  direction: ltr;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardNumber = styled.span<{isActive?: boolean}>`
  ${smallRegular}
  display: block;
  color: ${({isActive, theme}) => theme.colors[isActive ? 'contentDefault' : 'contentPlaceholder']};
`;

const StyledPieButton = styled(PieButton)`
  ${smallBold}

  .button-content-container {
    gap: 8px;
  }
`;

type TenbisCardProps = {
  cardHolderName: string;
  cardNumber: string;
  isActive: boolean;
  isRevealed: boolean;
  onShowDetailsClick?: (() => void) | (() => Promise<void>);
  onHideDetailsClick?: (() => void) | (() => Promise<void>);
  onActivateCardClick?: () => void;
  className?: string;
};

const TenbisCard = ({
  cardHolderName,
  cardNumber,
  onShowDetailsClick,
  onHideDetailsClick,
  onActivateCardClick,
  className,
  isActive,
  isRevealed,
}: TenbisCardProps) => {
  const {t} = getLocalizationService();
  const [isLoading, setIsLoading] = useState(false);

  const showDetailsHandler = async () => {
    setIsLoading(true);
    try {
      trackEvent('mcDetails_click');
      if (!isRevealed) {
        if (onShowDetailsClick) {
          await onShowDetailsClick();
        }
      }
      if (isRevealed && onHideDetailsClick) {
        if (onHideDetailsClick) {
          await onHideDetailsClick();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Root className={className}>
      <Background isActive={isActive}>
        <TenbisCardLogo />
      </Background>
      <ContentOverlay>
        <CardHolderNameContainer>
          <CardHolderName>{cardHolderName}</CardHolderName>
        </CardHolderNameContainer>
        <BottomBlock>
          <CardNumber isActive={isActive}>{cardNumber}</CardNumber>
          {isActive && (
            <StyledPieButton
              variant="outlineMedium"
              onClick={showDetailsHandler}
              isLoading={isLoading}
            >
              {isRevealed && <CircleTimer timeoutSeconds={60} size={15} strokeWidth={1.5} />}
              {t(isRevealed ? 'hide_details' : 'show_details')}
            </StyledPieButton>
          )}
          {!isActive && (
            <StyledPieButton variant="primaryMedium" onClick={onActivateCardClick}>{t('activate_card2')}</StyledPieButton>
          )}
        </BottomBlock>
      </ContentOverlay>
    </Root>
  );
};

export default memo(TenbisCard);
